/*
 * This file handles all storage related sideeffects.
 * As in: Everything about Local Storage, Cookies, IndexedDB etc.
*/

import { AnyAction, configureStore, createAction, createListenerMiddleware, ThunkDispatch, TypedAddListener } from '@reduxjs/toolkit'
import {
  userAuthenticated,
  userUnauthenticated,
  parametersUpdated,
  appRedirectionUpdated,
  sessionUpdated,
  Lang,
  langUpdated,
  Origin,
  originCleared,
} from '../../App/app';
import { authenticateUser } from '../api/authenticate-user';

import jwtDecode, { JwtPayload } from 'jwt-decode'
import { RootState } from '../store';
import { customChallenge, customSessionUpdated } from '../../pages/CustomSSO/custom-sso';
import { signOut } from '../../pages/Logout/logout';
import { redirectLogoutUri, redirectPKCE } from './redirect';
import {defaultClientId} from '../../client-ids';

export const initStorage = createAction( 'app/initStorage' );


const listenerMiddlewareStorage = createListenerMiddleware()

// Parameters

const prefix = 'hgskey:';


const keys = {
  localStorage: {
    authorization_code: `${ prefix }authorization_code`,
    state: `${ prefix }state`,
    client_id: `${ prefix }client_id`,
    redirect_app: `${ prefix }redirect_app`,
    redirect_uri: `${ prefix }redirect_uri`,
    redirect_origin: `${ prefix }redirect_origin`,
    logout_uri: `${ prefix }logout_uri`,
    origin: `${ prefix }origin`,
    lang: `${ prefix }lang`,
    macsxhost: `${ prefix }macsxhost`
  }
}

/**
 * Update the language of redirect_uri according to lang parameter. Temporary solution.
 * TODO: This should be replaced as soon as macs365 is going to handle languages
 * In url differntly / in a similar way like hgskey
 *
 * Update: Kept as it is for settings page language chnage. May need update when handling settings page
 * back button logic.

 * redirect_uri wont change based on language

const modifyRedirectUriLanguage = ( redirectUri: string | null, lang: Lang ): string => {

  if( redirectUri ) {
    let redirectUrl = new URL( redirectUri );
    redirectUrl.pathname = redirectUrl.pathname.replace(redirectUrl.pathname.split('/')[1], lang );
    redirectUri = redirectUrl.toString();
  }

  return redirectUri || '';
}

 */

const searchParameters = ( dispatch: ThunkDispatch<unknown, unknown, AnyAction> ) => {


    const referrer = !document.referrer.includes( location.hostname ) ? document.referrer : '';

    const params = new URLSearchParams( window.location.search );

    // Redirect uri

    // const redirect_uri = referrer ? referrer :
    //   ( params.get( 'redirect_uri' ) || localStorage.getItem( keys.localStorage.redirect_uri ) );

    let origin = ( params.get( 'origin' ) || '' ) as Origin;

    let macsxhost = ( params.get( 'macsxhost' ) || '' );

    if( origin ) {
      // params.delete( 'origin' );
    } else {
      origin = ( localStorage.getItem( keys.localStorage.origin ) || '' ) as Origin;
    }

    if(macsxhost) {
      // params.delete( 'macsxhost' );
    } else {
      macsxhost = ( localStorage.getItem( keys.localStorage.macsxhost ) || '' );
    }

    let redirect_uri = params.get('redirect_uri') || '';

    if( redirect_uri  ) {
      // Necessary for last step
      params.delete( 'redirect_uri' );
    } else {
      redirect_uri = localStorage.getItem( keys.localStorage.redirect_uri ) || referrer;
    }

    // Language

    let lang: Lang = params.get( 'lang' ) as Lang || '';

    if( lang ) {
      params.delete( 'lang' );

    } else {
      lang = localStorage.getItem( keys.localStorage.lang ) as Lang;
    }

    // Authorization code

    let authorization_code = params.get( 'authorization_code' ) || '';

    if( authorization_code ) {
      // params.delete( 'authorization_code' );
    } else {
      authorization_code = localStorage.getItem( keys.localStorage.authorization_code ) || '';
    }

    // State

    let state = params.get( 'state' ) || '';

    if( state ) {
      // params.delete( 'state' );
    } else {
      state = localStorage.getItem( keys.localStorage.state ) || '';
    }

    // Logout Uri

    let logout_uri = params.get( 'logout_uri' ) || '';
    if( logout_uri ) {
      params.delete( 'logout_uri' );
    } else {
      logout_uri = localStorage.getItem( keys.localStorage.logout_uri ) || '';
    }

    // add redirect_origin if redirect_uri is present
    let redirect_origin = redirect_uri ? new URL(redirect_uri).origin :
      localStorage.getItem( keys.localStorage.redirect_origin );

    // We save the client ID, our Amplify auth will be based on that one

    // TODO: Needs to be updated as soon as we need to integrate macsDIA
    let client_id = params.get( 'client_id' ) || '';
    if( client_id ) {
      params.delete( 'client_id' );
    } else {
      client_id = localStorage.getItem( keys.localStorage.client_id ) || defaultClientId;
    }

    // Url needs to be replaced with new query parameters
    // because we have a 2 sources of truth szenario here.
    // It is necessary or searchParameter state values which has been modified
    // will be overwritten again as soon as the page changes and searchParameters will
    // be executed again. Example: http://hgskey?lang=de -> searchParameters will be
    // executed -> state.app.searchParameters.redirect_uri = macs365.com/de/ ->
    // you change the language in the ui -> langChange is updated -> redirect_uri will
    // be modified -> state.app.searchParameters.redirect_uri = macs365.com/en/ -> user
    // signs in -> visits the authorize endpoint -> authorize endpoints redirects back
    // with the same query parameters from before -> lang is de again

    // location.hash is for FAQ page important

    window.history.replaceState( {}, '', `${ location.origin }${ location.pathname }${
      params.toString() ? `?${ params.toString() }${ location.hash }` : `${ location.hash }`
    }`)

    const redirectApp = localStorage.getItem( keys.localStorage.redirect_app );
    if( redirectApp ) dispatch( appRedirectionUpdated( redirectApp ) );

    localStorage.setItem( keys.localStorage.logout_uri, logout_uri || '' );
    localStorage.setItem( keys.localStorage.redirect_uri, redirect_uri || '' );
    localStorage.setItem( keys.localStorage.redirect_origin, redirect_origin || '' );
    localStorage.setItem( keys.localStorage.authorization_code, authorization_code || '' );
    localStorage.setItem( keys.localStorage.state, state || '' );
    localStorage.setItem( keys.localStorage.client_id, client_id || '' );
    localStorage.setItem( keys.localStorage.lang, lang || '');

    localStorage.setItem( keys.localStorage.origin, origin || '' );
    localStorage.setItem( keys.localStorage.macsxhost, macsxhost || '' );

    dispatch( parametersUpdated( {
      logout_uri: logout_uri || '',
      redirect_uri: redirect_uri || '',
      redirect_origin: redirect_origin || '',
      authorization_code: authorization_code || '',
      state: state || '',
      client_id: client_id || '',
      lang: lang || '',
      origin: origin || '',
      macsxhost: macsxhost || ''
    }) );
};


listenerMiddlewareStorage.startListening( {
  actionCreator: authenticateUser.pending,
  effect: async( action, listenerApi ) => {

    // Get only external referrers

    // 1. check referrer
    // 2nd check get redirect_uri parameter
    // 3rd check redirect_uri in localstorage
    // otherwise no redirection has been set === redirect to dashboard

    searchParameters( listenerApi.dispatch );


  }
})

// Clear authorisation_code from local storage after it has been used

listenerMiddlewareStorage.startListening( {
  actionCreator: redirectPKCE,
  effect: async( action, listenerApi ) => {
    localStorage.setItem( keys.localStorage.authorization_code, '' );
  }
})

// Clear logout_uri from local storage after it has been used

// listenerMiddlewareStorage.startListening( {
//   actionCreator: redirectLogoutUri,
//   effect: async( action, listenerApi ) => {
//     localStorage.setItem( keys.localStorage.logout_uri, '' );
//   }
// })


listenerMiddlewareStorage.startListening( {
  actionCreator: appRedirectionUpdated,
  effect: async( action, listenerApi ) => {

    localStorage.setItem( keys.localStorage.redirect_app, action.payload )

  }
} )

// App signout / user Unauthenticated

// Why are we checking in the actionCreator userUnauthenticated
// instead of signOut? SignOut should only been fired
// when user wanted to sign out. userUnauthenticated will be fired
// when we want to clear all the user / app data.


listenerMiddlewareStorage.startListening( {
  actionCreator: signOut.pending,
  effect: async( action, listenerApi ) => {
    listenerApi.dispatch( userUnauthenticated() )
    // TODO: checkout how to handle client_id, this needs to be stored
    // even after a logout
    // localStorage.clear();

  }
} )

listenerMiddlewareStorage.startListening( {
  actionCreator: userUnauthenticated,
  effect: async( action, listenerApi ) => {

    // Don't clear the whole localStorage
    // certain keys such as client_id
    // needs to be stored still
    localStorage.removeItem( keys.localStorage.redirect_uri );
    localStorage.removeItem( keys.localStorage.redirect_app );
    localStorage.removeItem( keys.localStorage.authorization_code );

    // We are erasing the cookies so that the normal login after
    // custom sso dont trigger the token swap scenario in authorize endpoint
    // with invalidated tokens in cookies
    eraseCookie("refresh_token");
    eraseCookie("id_token");
    eraseCookie("access_token");
  }
} )

// If we have in parameter that means start a PKCE or Implict flow
// We switch the clientId to submit the one from the client website
listenerMiddlewareStorage.startListening( {
  actionCreator: initStorage,
  effect: async( action, listenerApi ) => {

    searchParameters( listenerApi.dispatch );

  }
} )

listenerMiddlewareStorage.startListening( {
  actionCreator: langUpdated,
  effect: async( action, listenerApi ) => {

    localStorage.setItem( keys.localStorage.lang, action.payload );

  }
} )

listenerMiddlewareStorage.startListening( {
  actionCreator: customSessionUpdated,
  effect: async( action, listenerApi ) => {

    const state = listenerApi.getState() as RootState;
    const {
      idToken,
      accessToken,
      refreshToken,
    } = state.pages.customSSO.session;

    // Amplify requires cookies

    setTokenCookie( 'id_token', idToken );
    setTokenCookie( 'access_token', accessToken );
    setRefreshTokenCookie( refreshToken, accessToken );

  }
} )

listenerMiddlewareStorage.startListening( {
  actionCreator: sessionUpdated,
  effect: async( action, listenerApi ) => {

    const state = listenerApi.getState() as RootState;

    const { idToken, accessToken, refreshToken } = state.app.session;

		if ( idToken && accessToken && refreshToken) {
			setTokenCookie("id_token", idToken);
			setTokenCookie("access_token", accessToken);
			setRefreshTokenCookie(refreshToken, accessToken);
		}

  }
} )

listenerMiddlewareStorage.startListening( {
  actionCreator: originCleared,
  effect: async( action, listenerApi ) => {

    localStorage.removeItem( keys.localStorage.origin );

  }
} )

const getCookie = ( name: string ) => {
	var nameEQ = name + "=";
	var ca = document.cookie.split( ';' );
	for (var i = 0; i < ca.length; i++) {
		var c = ca[i];
		while (c.charAt(0) === ' ') c = c.substring(1, c.length);
		if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
	}
	return null;
}

const setCookie = ( name: string, value: string = '', expiry: number ) => {
	let expires = "";
	if (expiry) {
		const date = new Date(expiry * 1000);
		// expires = `; expires=${ date.toUTCString() }`;
    expires = "; expires=" + date.toUTCString();
	}

  document.cookie = name + "=" + (value || "") + expires + "; path=/" + "; SameSite=None" + "; Secure";
}


const setSameSiteLaxCookie = ( name: string, value: string = '', expiry: number ) => {
	let expires = "";
	if (expiry) {
		const date = new Date(expiry * 1000);
		// expires = `; expires=${ date.toUTCString() }`;
    expires = "; expires=" + date.toUTCString();
	}

  document.cookie = name + "=" + (value || "") + expires + "; path=/" + "; SameSite=Lax" + "; Secure";
}

const eraseCookie = ( name: string ) => {
  console.log('eraseCookie', name);
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
	// document.cookie = `${ name }=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}

const setTokenCookie = ( type: string, token: string ) => {
  const tokenDecoded = jwtDecode<JwtPayload>( token );
	let tokenExpiry = tokenDecoded['exp'];
	setSameSiteLaxCookie(type, token, tokenExpiry || 0 );
}

const setRefreshTokenCookie = (refreshToken: string, accessToken: string ) => { // Use expiry of access token to set the refresh token cookie
  const tokenDecoded = jwtDecode<JwtPayload>( accessToken );

	let tokenExpiry = tokenDecoded['exp'];
	setSameSiteLaxCookie("refresh_token", refreshToken, tokenExpiry || 0 );
}

// const getOriginFromUri = ( inputURI : string ) => {
//   let inputURIObj = new URL(inputURI);
//   return inputURIObj.origin;
// }


export default listenerMiddlewareStorage;

